
import './record-edit-screen.scss'

import _ from 'lodash'
import { set } from 'lodash'
import { forkJoin, of, empty } from 'rxjs'
import { catchError, first, switchMap, tap, take } from 'rxjs/operators'
import { faArrowLeft, faEye, faTabletAlt } from '@fortawesome/free-solid-svg-icons'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

import { AuditCampaign, RecordMask, Item, AuditCampaignFeatured, SearchFeatured, Record, RecordLifecycleState, RecordFeatured, EntityFieldStatus, EntityField } from '@skinsoft/types'
import { auditRecord_recordMask } from '@skinsoft/record-mask-configs'
import { itemSelectors, locationSelectors, recordSelectors } from '@skinsoft/selectors'
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { AuditCampaignDataStore, ItemDataStore, LocationDataStore, RecordDataStore } from '@skinsoft/entity'
import { toCampaignFeatured$, toRecordFeatured$ } from '@skinsoft/entity-ui'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { MaskFieldUpdateEventType } from '@skinsoft/record-mask/src/components/mask-field'
import { RecordMaskDisplay } from '@skinsoft/record-mask'
import { toSearchFeatured$ } from '@skinsoft/search-data-store/src/featured'
import { CollapsibleHeader } from '@skinsoft/ui'
import { Media } from '@skinsoft/ui/src/item-card/media'

import { FORCE_CLOSE_MENU_TOPIC, SAVE_CURRENT_RECORD_TOPIC } from '../../../busTopics'
import { TRANSITION_RECORD_TO_STATE } from '../../../events/events'
import { TransitionRecordToStateEvent } from '../../../events/types'
import { Routes } from '../../../router'
import { AuditItemEditScreen } from '../audit-item-edit-screen'
import { AddMediaEvent } from '@skinsoft/entity-ui/src/media/medias-fields-area'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'

declare const OFFLINE_DISABLED_MASK_FIELDS: { entityId: string, message?: string }[]

@Component({
    components: {
        sCollapsibleHeader: CollapsibleHeader,
        sAuditItemEditScreen: AuditItemEditScreen,
        sMedia: Media,
        sRecordMaskDisplay: RecordMaskDisplay
    },
    props: ['recordId', 'campaignId'],
    template: require('./record-edit-screen.html')
})
export class RecordEditScreen extends ReactiveVue {

    public recordId: string;
    public campaignId: string;

    private campaign: AuditCampaign = null as any;
    private record: Record = null as any;
    private item: Item = null as any;

    // Copy of the record used for saving, it avoids unnecessary rerender of components
    private recordToSave: Record = null as any;

    private campaignFeatured: AuditCampaignFeatured = null as any;
    private recordFeatured: RecordFeatured = null as any;
    private itemFeatured: SearchFeatured = null as any;

    private itemLocation: string = "";
    private displayItemScreen: boolean = false;
    private overrideFromUpdateCache = false;

    private recordMask: RecordMask = auditRecord_recordMask;

    private offlineDisabledMaskFields: { entityId: string, message?: string }[]
    private isOnline: boolean = window.navigator.onLine
    private fieldsErrors: { entityId: string, message: string }[] = []

    // Icons 
    private backIcon = faArrowLeft;
    private eyeIcon = faEye;
    private localIcon = faTabletAlt;

    private $auditCampaignDataStore: AuditCampaignDataStore
    private $recordDataStore: RecordDataStore;
    private $itemDataStore: ItemDataStore;
    private $locationDataStore: LocationDataStore;
    private $mediaTransferHelper: MediaTransferHelper;
    private $imageTransferHelper: ImageTransferHelper

    private saveSuccessMessage: string = ""
    private saveErrorMessage: string = ""

    public mounted() {
        this.subscribeToBus(SAVE_CURRENT_RECORD_TOPIC, (event) => this.saveMediaAndRecord())
        this.subscribeToBus(TRANSITION_RECORD_TO_STATE, (event) => this.transitionRecord(event.payload))
        this.saveSuccessMessage = this.$t('editRecordScreen.notifications.saveSuccess') as string;
        this.saveErrorMessage = this.$t('editRecordScreen.notifications.saveError') as string;

        this.offlineDisabledMaskFields = OFFLINE_DISABLED_MASK_FIELDS.map(disabledMaskField => {
            if(disabledMaskField.message) {
                disabledMaskField.message = this.$t(disabledMaskField.message) as string
            }

            return disabledMaskField
        })

        // we use event listener's for connectivity status because it enable to have components that updates when the network status changes
        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)
    }

    public beforeDestroy() {
        window.removeEventListener('online', this.updateConnexionStatus)
        window.removeEventListener('offline', this.updateConnexionStatus)
        super.beforeDestroy();
    }

    private updateConnexionStatus() {
        this.isOnline = window.navigator.onLine
    }

    private saveMediaAndRecord() {
        /*const medias$ = (_.get(this.record,'associatedMedias.medias', []) ?? [])
            .filter((media: String) => {
                if(media instanceof String || typeof media === 'string') {
                    return !!media
                } else {
                    const mediaId = _.get(media, 'ref') ?? ''
                    return !!mediaId
                }
            })
            .map(media => {
                let mediaId: string = ''
                if(media instanceof String || typeof media === 'string') {
                    mediaId = media as string
                } else {
                    mediaId = _.get(media, 'ref') ?? ''
                }

                if(window.navigator.onLine && MediaTransferHelper.isLocalMedia(mediaId)) {
                    return this.$imageTransferHelper.downloadImage(mediaId).pipe(take(2))
                }
                return of(mediaId)
            })

        forkJoin(medias$).subscribe( 
            () => {
                _.set(this.recordToSave,'associatedMedias',  _.get(this.record,'associatedMedias'))
                this.saveRecord()
            }, 
            () => {},
            () => {
                _.set(this.recordToSave,'associatedMedias',  _.get(this.record,'associatedMedias'))
                this.saveRecord()
            }
        )*/
        this.saveRecord()
    }

    private saveRecord() {
        this.$recordDataStore.update(this.recordId, this.recordToSave)
            .pipe(first(),
                catchError(
                    err => {
                        const alertPayload = {
                            message: this.saveErrorMessage,
                            variant: "danger"
                        };
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        return empty()
                    }
                ))
            .subscribe(
                response => {
                    this.overrideFromUpdateCache = !!response.fromUpdateCache;

                    const alertPayload = {
                        message: this.saveSuccessMessage,
                        variant: "success"
                    };

                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                }
            );
    }

    private subscribe() {
        if (this.campaignId && this.recordId) {
            const locale = this.getAppLocale()
            this.subscribeTo(
                this.$auditCampaignDataStore.fetchOne(this.campaignId).pipe(
                    switchMap(campaign => toCampaignFeatured$(campaign, locale))
                ),
                campaignFeatured => {
                    this.campaignFeatured = campaignFeatured;
                },
                'campaign-subscription')

            this.subscribeTo(
                this.$recordDataStore.fetchOne(this.recordId).pipe(
                    tap(
                        record => {
                            this.record = record
                            this.recordToSave = _.cloneDeep(record);
                        }
                    ),
                    switchMap(record => {
                        return toRecordFeatured$(record, locale)
                    }),
                    switchMap( // Keeping record data
                        recordFeatured => {
                            this.recordFeatured = recordFeatured;
                            return this.$itemDataStore.fetchOne(recordFeatured.linkedItemId)
                        }
                    ),
                    tap( // Fetching location
                        async item => {
                            const locId = itemSelectors.realTimeLocation(item);
                            if (locId) {
                                const loc = await (this.$locationDataStore.fetchOne(locId).pipe(first()).toPromise());
                                this.itemLocation = locationSelectors.translatedTitle(
                                    loc, 
                                    this.getAppLocale()
                                )
                            }
                        }
                    ),
                    switchMap(item => { // Keeping item data
                        this.item = item;
                        return toSearchFeatured$({
                            expression: null,
                            item,
                            manifestation: null,
                            work: null,
                        }, locale, false)
                    })
                ),
                itemFeatured => {
                    this.itemFeatured = itemFeatured;
                },
                'record-subscription')
        }
    }

    private unsubscribe() {
        this.unsubscribeFrom('record-subscription')
        this.unsubscribeFrom('campaign-subscription')
    }

    private get fromUpdateCache() {
        return this.recordFeatured?.fromUpdateCache || this.overrideFromUpdateCache;
    }

    private transitionRecord(event: TransitionRecordToStateEvent) {
        const errorMessage = this.$t('editRecordScreen.notifications.stateTransitionError');
        const sucessMessage = this.$t('editRecordScreen.notifications.stateTransitionSuccess');
        this.$recordDataStore.transitionRecord(this.record.id, event.state, event.translations)
            .then(
                () => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: sucessMessage,
                        variant: "success"
                    })
                    this.goBack()
                }
            ).catch(
                () => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: errorMessage,
                        variant: "danger"
                    })
                }
            )
    }

    private openItemEditScreen() {
        this.saveMediaAndRecord();
        this.displayItemScreen = true
    }

    private updateRecord(updateEvent) {
        if(updateEvent.type === MaskFieldUpdateEventType.DELETE) {
            // In case of a deletion, we need to check if the deleted value
            // is inside an array. Indeed, we have to use 2 differents approaches
            // depending if we delete a field or a value inside an array.

            const lastPropInPathRegex = /(\[\d\]|\.[a-zA-Z\d]*)$/

            const path = updateEvent.fieldPath
            const pathToLastParent = path.replace(lastPropInPathRegex, '')
            const lastParent = _.get(this.recordToSave, pathToLastParent)

            if(_.isArray(lastParent)) {
                // if the last parent is an array, we extract the index of the value
                // to delete from the path (it is the last "prop" in the path)

                const indexMatches = path.match(lastPropInPathRegex)
                if(indexMatches?.length) {
                    // @ts-ignore need to upgrade ts (replaceAll)
                    const indexString = indexMatches[0].replaceAll(/\[|\]|\./g, '')
                    const index = parseInt(indexString)
                    if(!isNaN(index)) {
                        lastParent.splice(index, 1)
                    } else {
                        // in case of error the user will see no action from his deletion, so no need
                        // to show an error message
                    }

                } else {
                    // in case of error the user will see no action from his deletion, so no need
                    // to show an error message
                }

            } else {
                _.unset(this.recordToSave, path)
            }
        } else {
            // update the field with event data
            set(this.recordToSave, updateEvent.fieldPath, updateEvent.value)
        }
    }

    private goBack() {
        this.$router.push({ name: Routes.CAMPAIGN, params: { campaignId: this.campaignId } });
        this.emitOnBus(FORCE_CLOSE_MENU_TOPIC)
    }

    private get itemPath() {

        if (this.campaignFeatured && this.recordFeatured && this.itemFeatured) {
            return `${this.campaignFeatured.identificationNumber} > ${this.recordFeatured.recordIdentifier} > ${this.itemFeatured.inventoryNumber} `
        }
    }

    private get isReadOnly() {
        const recordState = recordSelectors.currentState(this.record, this.getAppLocale())
        return recordState === 'validated' || recordState === 'toValidated'
    }

    private get status() {
        const translation = this.$t(`state.${this.recordFeatured.currentState}`)
        // if there is no translation (added to fix status in offline mode)
        if(translation === `state.${this.recordFeatured.currentState}`) {
            return this.recordFeatured.currentState
        }

        return translation
    }

    @Watch('$route', { immediate: true })
    private onRouteChange() {
        this.unsubscribe();
        this.subscribe();
    }

    private onAddMedia(addedMedia: AddMediaEvent){
        const medias = _.get(this.recordToSave, 'associatedMedias.medias', null)

        if(addedMedia.newMediaId) {
            if(medias === null){
                _.set(this.recordToSave, 'associatedMedias.medias', [addedMedia.newMediaId])
            }else{
                const mediasWithoutCachedVersion = medias.filter(mediaId => mediaId !== addedMedia.cacheKey)
                _.set(this.recordToSave, 'associatedMedias.medias', mediasWithoutCachedVersion)
                _.get(this.recordToSave, 'associatedMedias.medias', []).push(addedMedia.newMediaId)
            }

        } else if(!medias.includes(addedMedia.cacheKey)) {
            if(medias === null){
                _.set(this.recordToSave, 'associatedMedias.medias', [addedMedia.cacheKey])
            }else{
                _.get(this.recordToSave, 'associatedMedias.medias', []).push(addedMedia.cacheKey)
            }
        }
    }

    private getEntityFieldStatus(entityField: EntityField): EntityFieldStatus {
        let result: EntityFieldStatus = {
            disabled: false,
            error: false
        }

        if(!this.isOnline) {
            const disabledEntityField = this.offlineDisabledMaskFields?.find(disabledField => {
                return disabledField.entityId === (entityField as any).entityFieldId 
                // id is not always present on entityField but entityFieldId is always
            })
    
            if(disabledEntityField) {
                result.disabled = true
                if(disabledEntityField.message) {
                    result.disabledMessage = disabledEntityField.message
                }
            }
        }
        
        const errorEntityField = this.fieldsErrors?.find(error => {
            return error.entityId === (entityField as any).entityFieldId
        })

        if(errorEntityField) {
            result.error = true
            result.errorMessage = errorEntityField.message
        }

        return result
    }

}